import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getTakeoverCampaignMessageMedium, getTakeoverExperimentType } from '../ducks/selectors';
import { MutatedExperimentVariant } from '../takeover/types';
import { FormSchemaType } from '../types';
import { getDefaultSendTimeFieldValues, getVariantFormFieldValue } from '../utils';
import { useTakeoverExperimentContext } from './TakeoverExperimentContext';
import { useVariantConfigurationContext } from './VariantConfigurationContext';

type VariantCardFormProviderProps = {
  variant: MutatedExperimentVariant;
};

export const VariantCardFormProvider: FC<React.PropsWithChildren<VariantCardFormProviderProps>> = ({
  children,
  variant,
}) => {
  const isMountRef = useRef(false);
  const {
    isSendTimeExperiment,
    sendTimeMetaInfo: { controlStartTime, sendTimeOfAllVariants, sendTimeStartTimeZone },
  } = useTakeoverExperimentContext();
  const type = useSelector(getTakeoverExperimentType);
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const { formFieldZodSchema } = useVariantConfigurationContext();
  const defaultValues = isSendTimeExperiment
    ? getDefaultSendTimeFieldValues({
        variant,
        controlStartTime,
        sendTimeOfAllVariants,
        sendTimeStartTimeZone,
      })
    : getVariantFormFieldValue({ variant, type, messageMedium });
  const methods = useForm<FormSchemaType<typeof formFieldZodSchema>>({
    defaultValues,
    resolver: zodResolver(formFieldZodSchema),
    mode: 'all',
  });

  /**
   * Validate default values on mount.
   * This is especially done because `isDirty` is not set to true until the user interacts with the form,
   * but we want to allow users to submit the form with the default values.
   * @see https://github.com/react-hook-form/react-hook-form/issues/2755
   */
  useEffect(() => {
    methods.trigger();
  }, []);

  /**
   * This will reset the default state of the form
   * each time an update is done. And, also trigger a form revalidation
   * We also stringify the variant to run the effect only for
   * the given variant which change. If not all our cards will update
   */
  useEffect(() => {
    if (isMountRef.current) {
      const updatedFormValues = isSendTimeExperiment
        ? getDefaultSendTimeFieldValues({
            variant,
            controlStartTime,
            sendTimeOfAllVariants,
            sendTimeStartTimeZone,
          })
        : getVariantFormFieldValue({ variant, type, messageMedium });
      methods.reset(updatedFormValues);
      methods.trigger();
    }
    isMountRef.current = true;
  }, [JSON.stringify(variant)]);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
